import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger, useSidebar } from "@pps/ui/components/ui/sidebar";
import { AppSidebar } from '@/components/AppSidebar';
import { Separator } from '@pps/ui/components/ui/separator';
import { Button } from '@pps/ui/components/ui/button';
import { PlanUpgradeDialog } from '@/components/PlanUpgradeDialog';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageToggle } from '@/components/LanguageToggle';
import { NotificationBell } from '@/components/NotificationBell';
import { HeaderOverflowMenu } from '@/components/HeaderOverflowMenu';
import { ArrowUpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SocialNotifications } from '@/components/SocialNotifications';
import { HelpButton } from '@/components/help/HelpButton';
import { DailyActivitiesWidget } from '@/components/DailyActivitiesWidget';
import { TrialStatusBanner } from '@/components/TrialStatusBanner';
import { ProfessorSemCadastroBanner } from '@/components/teachers/ProfessorSemCadastroBanner';
import { VoltarNoCabecalho } from '@/components/VoltarNoCabecalho';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'pps.crm.sidebar.collapsed';

function MobileDrawerCloser() {
  const { isMobile, openMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  useEffect(() => {
    if (isMobile && openMobile) {
      setOpenMobile(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
}

function readInitialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AppLayout({ children }: { children: ReactNode }) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => !readInitialCollapsed());
  const { t } = useTranslation();

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(!sidebarOpen));
    } catch {
      // ignore quota / private mode errors
    }
  }, [sidebarOpen]);

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
      <MobileDrawerCloser />
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Banners + cabeçalho fixos no topo, com a área segura do topo no
              bloco (status bar translúcida do iPhone — ver index.html): no PWA
              o hambúrguer e o Voltar são a única navegação e não podem rolar
              para fora da tela, e o banner não pode ficar sob o relógio. */}
          <div className="sticky top-0 z-30 bg-card pt-[env(safe-area-inset-top)] shrink-0">
          <TrialStatusBanner />
          <ProfessorSemCadastroBanner />
          <header className="h-14 flex items-center gap-1 sm:gap-2 border-b pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:px-4 bg-card">
            <VoltarNoCabecalho />
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-5" />
            <div className="flex-1" />
            <SocialNotifications />
            <HelpButton />
            <NotificationBell />
            <div className="hidden md:flex items-center gap-1">
              <LanguageToggle />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setUpgradeOpen(true)}
              >
                <ArrowUpCircle className="h-3.5 w-3.5" />
                {t('common.changePlan')}
              </Button>
            </div>
            {/* Abaixo de 768px os tres controles acima nao cabem na barra —
                antes eram simplesmente escondidos e idioma/tema ficavam sem
                NENHUMA entrada no app (achado do rapa de 19/08/2026). Aqui
                eles viram um menu "mais opcoes". */}
            <div className="md:hidden">
              <HeaderOverflowMenu onOpenPlanUpgrade={() => setUpgradeOpen(true)} />
            </div>
          </header>
          </div>
          {/*
            Padding inferior reserva safe-area para o FAB global
            `DailyActivitiesWidget` (fixed bottom-6 right-6 h-14 = ~88px).
            Sem isso, botoes de acao "Salvar" alinhados a direita no rodape
            de paginas (ex.: Configuracoes > Clube) ficam atras do FAB e
            inclicaveis (bug reportado pelo Fabio). 6rem = 96px cobre
            o FAB com folga; soma a area segura de baixo (home indicator do
            iPhone) para o FAB, que tambem a respeita (IR-UX-003).
          */}
          <main className="flex-1 overflow-hidden pb-[calc(6rem+env(safe-area-inset-bottom))]">
            {children}
          </main>
        </div>
      </div>
      <PlanUpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <DailyActivitiesWidget />
    </SidebarProvider>
  );
}
